/**
 * Document-type registry — deliberately zod-free.
 *
 * This module is exposed as the `@thesitebook/contracts/document-meta`
 * subpath so CLIENT components can import label helpers without dragging the
 * whole CommonJS contracts package (every entity/wizard zod schema) into the
 * browser chunk. Server code may import these via the package root, which
 * re-exports everything here through `entities/document.ts`.
 *
 * This is the single source of truth for the per-type display/config values
 * that used to live in ~10 hand-maintained maps across web, mobile and lib
 * code. Adding a COSHH-style new DocumentType previously meant finding every
 * one of those maps; the ones that were missed fell back silently (raw enum
 * names in the UI / filenames) or 500'd (the contracts enum itself was missed
 * on the first COSHH pass). `satisfies Record<DocumentType, DocumentTypeMeta>`
 * makes a missing or extra entry a COMPILE error, so a new enum member cannot
 * ship without deciding its metadata.
 *
 * Deliberately NOT in this registry: business/gating rules (which types a
 * safety profile gates, mobile wizard availability, plan entitlements) — those
 * are policy, not metadata, and live with their features.
 */

export const DOCUMENT_TYPES = [
  "RAMS",
  "CPP",
  "TOOLBOX_TALK",
  "SITE_INDUCTION",
  "EMERGENCY_PLAN",
  "COSHH",
  "SIGN_OFF",
] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export interface DocumentTypeMeta {
  /** Human-readable label (web display convention, e.g. "Toolbox Talk"). */
  label: string;
  /**
   * Filename-safe hyphenated label used in generated pack/export filenames
   * (e.g. "Toolbox-Talk" inside audit-pack merges).
   */
  filenameLabel: string;
  /**
   * URL segment of the web generation wizard: /projects/:id/generate/<segment>.
   * Values are historical and deliberately inconsistent (hyphens vs
   * underscores) — they are live URLs and must not be "tidied".
   */
  generateSegment: string;
  /**
   * Whether the web generate route accepts this type. SIGN_OFF is
   * system-generated via its own plots/trades flow, never the generate route.
   */
  generatable: boolean;
}

export const DOCUMENT_TYPE_META = {
  RAMS: {
    label: "RAMS",
    filenameLabel: "RAMS",
    generateSegment: "rams",
    generatable: true,
  },
  CPP: {
    label: "CPP",
    filenameLabel: "CPP",
    generateSegment: "cpp",
    generatable: true,
  },
  TOOLBOX_TALK: {
    label: "Toolbox Talk",
    filenameLabel: "Toolbox-Talk",
    generateSegment: "toolbox-talk",
    generatable: true,
  },
  SITE_INDUCTION: {
    label: "Site Induction",
    filenameLabel: "Site-Induction",
    generateSegment: "site_induction",
    generatable: true,
  },
  EMERGENCY_PLAN: {
    label: "Emergency Plan",
    filenameLabel: "Emergency-Plan",
    generateSegment: "emergency_plan",
    generatable: true,
  },
  COSHH: {
    label: "COSHH Assessment",
    filenameLabel: "COSHH-Assessment",
    generateSegment: "coshh",
    generatable: true,
  },
  SIGN_OFF: {
    label: "Sign-off",
    filenameLabel: "Sign-Off",
    generateSegment: "sign_off",
    generatable: false,
  },
} as const satisfies Record<DocumentType, DocumentTypeMeta>;

/** The types the web generate route accepts (everything except SIGN_OFF). */
export const GENERATABLE_DOCUMENT_TYPES: DocumentType[] = DOCUMENT_TYPES.filter(
  (t) => DOCUMENT_TYPE_META[t].generatable,
);

/**
 * Tolerant label lookups: Prisma rows type `documentType` as string at several
 * call sites, and legacy rows/fallbacks must render the raw enum name rather
 * than crash — matching the `?? type` fallback every migrated map had.
 */
export function documentTypeLabel(type: string): string {
  return (
    (DOCUMENT_TYPE_META as Record<string, DocumentTypeMeta>)[type]?.label ?? type
  );
}

export function documentTypeFilenameLabel(type: string): string {
  return (
    (DOCUMENT_TYPE_META as Record<string, DocumentTypeMeta>)[type]?.filenameLabel ??
    type
  );
}
